import { useApplication } from '@eneco/common/application';
import RichText from '@eneco/components/RichText/RichText';
import { DeliveryCountdownCardResponse } from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
import { Box, Heading, Stack, Text } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { DashboardOverviewRendering } from '@sitecore/types/DashboardOverview';

import { StyledDashboardBlock } from './DashboardBlock';
import { EnergyIcon } from '../../advance/components/EnergyIcon';

export const DeliveryCountDownCard = ({ card }: { card: DeliveryCountdownCardResponse }) => {
  const { details } = card;
  const { fields } = useContent<DashboardOverviewRendering>();
  const { format, address, date } = useFormatter();
  const { language } = useApplication();

  const formattedAddress = address.medium({
    street: details.address.street,
    houseNumber: details.address.houseNumber,
    houseNumberSuffix: details.address.bus,
    postalCode: details.address.postalCode,
    city: details.address.municipality,
  });

  const energyTypes = [
    details.electricityEan && fields.deliveryCountdownCard?.electricityTitle?.value?.toLowerCase(),
    details.gasEan && fields.deliveryCountdownCard?.gasTitle?.value?.toLowerCase(),
  ].filter(Boolean);

  const description = format(fields.deliveryCountdownCard?.description?.value || '', {
    energyTypes: energyTypes.join(language === 'fr' ? ' et ' : ' en '),
    address: formattedAddress,
    startDate: date.short(card.details.startDate ?? new Date()),
  });

  return (
    <StyledDashboardBlock>
      <RichText html={description} />
      <Stack gap={4}>
        <Heading size="3XS" as="h3">
          {fields.deliveryCountdownCard?.productsTitle?.value}
        </Heading>
        <Stack gap={2}>
          {details.gasEan && (
            <Box padding={4} backgroundColor="backgroundSecondary" borderRadius="s">
              <Stack direction="row" alignY="center" gap={4}>
                <Stack.Item shrink={false}>
                  <Box paddingY={2} paddingX={5} backgroundColor="backgroundPrimary" borderRadius="s">
                    <EnergyIcon energyType="Gas" size="medium" />
                  </Box>
                </Stack.Item>
                <Stack>
                  <Text weight="bold" size="BodyS">
                    {fields.deliveryCountdownCard?.gasTitle?.value}
                  </Text>
                  <Text>{details.gasEan}</Text>
                </Stack>
              </Stack>
            </Box>
          )}
          {details.electricityEan && (
            <Box padding={4} backgroundColor="backgroundSecondary" borderRadius="s">
              <Stack direction="row" alignY="center" gap={4}>
                <Stack.Item shrink={false}>
                  <Box paddingY={2} paddingX={5} backgroundColor="backgroundPrimary" borderRadius="s">
                    <EnergyIcon energyType="Electricity" size="medium" />
                  </Box>
                </Stack.Item>
                <Stack>
                  <Text weight="bold" size="BodyS">
                    {fields.deliveryCountdownCard?.electricityTitle?.value}
                  </Text>
                  <Text>{details.electricityEan}</Text>
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </Stack>
    </StyledDashboardBlock>
  );
};
